import { useState } from 'react';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  InputGroup,
  NativeSelect,
  Portal,
  Text,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import { capitalizeFirstLetter } from '@utils/formats';
import { toast } from 'react-toastify';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';

import type { OptionType } from '../types';
import useGetBehaviours from '../useGetBehaviours';
import Loading from '@components/Loading';

interface Props {
  handleClose: () => void;
  afterSubmit: () => void;
  option: OptionType,
};

const EditDialog = ({ handleClose, afterSubmit, option }: Props) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors | null>(null);
  const [selectedBehaviour, setSelectedBehaviour] = useState(option.behaviour);

  const { behaviours, loading: loadingBehaviours } = useGetBehaviours();

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());

    if (selectedBehaviour === null) {
      setErrors({ behaviour: ['É necessário selecionar um comportamento.'] });
      return;
    }

    setLoading(true);

    try {
      const response = await api.put(`/disposition_option/${option.id}`, {
        name: capitalizeFirstLetter(data.name as string),
        is_enabled: option.is_enabled,
        behaviour: selectedBehaviour,
      });

      if (response?.status === 200) {
        toast.success('Opção de tabulação editada com sucesso!');
        afterSubmit();
        handleClose();
      } else if (response?.data?.errors) {
        setErrors(response.data.errors);
      }
    } catch {
      toast.error('Erro ao editar opção de tabulação. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    };
  };

  return (
    <Dialog.Root size="md" placement="center" open onOpenChange={handleClose} closeOnInteractOutside={false}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>Nova opção de tabulação</Dialog.Title>
            </Dialog.Header>

            {(loadingBehaviours || behaviours.length < 1) ? <Loading /> : (
              <form onSubmit={onSubmit}>
                <Dialog.Body>
                  <Field.Root invalid={!!errors?.name}>
                    <Field.Label>Nome</Field.Label>
                    <InputGroup width="full" gap={0}>
                      <Input
                        name="name"
                        placeholder="Digite aqui..."
                        defaultValue={option.name}
                        minLength={3}
                        variant="subtle"
                        required
                      />
                    </InputGroup>
                    {errors?.name?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>
                  <Box mt={4}>
                    <Text fontWeight={500} mb={1}>Comportamento</Text>
                    <NativeSelect.Root size="md" variant="subtle" invalid={!!errors?.behaviour}>
                      <NativeSelect.Field
                        placeholder="Selecione uma opção"
                        value={selectedBehaviour}
                        onChange={(e) => setSelectedBehaviour(Number(e.currentTarget.value))}
                      >
                        {behaviours.map((behaviour) => (
                          <option key={behaviour.id} value={behaviour.id}>
                            {behaviour.name}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    {errors?.behaviour?.map((error, index) => (
                      <ErrorLabel key={index} text={error} />
                    ))}
                  </Box>
                  {errors?.general && (
                    <Flex direction="column" my={4}>
                      {errors.general.map((error, index) => (
                        <ErrorLabel key={index} text={error} />
                      ))}
                    </Flex>
                  )}
                </Dialog.Body>

                <Dialog.Footer>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="outline" disabled={loading}>
                      Cancelar
                    </Button>
                  </Dialog.ActionTrigger>
                  <Button bg="green.700" color="white" type="submit" loading={loading}>
                    Salvar
                  </Button>
                </Dialog.Footer>
              </form>
            )}
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default EditDialog;
